import React from "react";
import { Link } from "react-router-dom";
import { Package, Palette, ShoppingBag, TrendingUp, ArrowRight, Clock, Truck, CheckCircle2, Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useUserData } from "../../hooks/useUserData";

const statusIcon = {
  "In Production": Clock,
  "Shipped": Truck,
  "Delivered": CheckCircle2,
  "Cancelled": Package,
};
const statusBg = { amber: "bg-amber-100 text-amber-700", blue: "bg-blue-100 text-blue-700", emerald: "bg-emerald-100 text-emerald-700", zinc: "bg-zinc-200 text-zinc-600" };

const Overview = () => {
  const { user } = useAuth();
  const { orders, designs } = useUserData();
  const recent = orders.slice(0, 3);
  const totalSpend = orders.filter((o) => o.status !== "Cancelled").reduce((s, o) => s + (o.total || 0), 0);
  const inProgress = orders.filter((o) => ["In Production", "Shipped"].includes(o.status)).length;

  return (
    <div>
      {/* Greeting */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900">Hi {user.firstName}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {orders.length === 0
              ? "Welcome to Design Plus — let's get your first print job started."
              : "Here's a quick look at your Design Plus activity."}
          </p>
        </div>
        <Link to="/shop" className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-3 rounded-lg text-sm font-bold hover:bg-rose-500 transition-colors">
          <ShoppingBag className="w-4 h-4" /> Start a new order
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[
          { icon: Package, label: "Total orders", value: orders.length, accent: "text-rose-500 bg-rose-50" },
          { icon: Clock, label: "In progress", value: inProgress, accent: "text-amber-600 bg-amber-50" },
          { icon: Palette, label: "Saved designs", value: designs.length, accent: "text-violet-600 bg-violet-50" },
          { icon: TrendingUp, label: "Lifetime spend", value: `$${totalSpend.toFixed(0)}`, accent: "text-emerald-600 bg-emerald-50" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-zinc-200 p-5">
            <div className={`w-10 h-10 rounded-lg ${s.accent} flex items-center justify-center mb-3`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold text-zinc-900">{s.value}</div>
            <div className="text-xs text-zinc-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent orders */}
      <div className="bg-white rounded-2xl border border-zinc-200 mt-6">
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
          <h2 className="font-bold text-zinc-900">Recent orders</h2>
          {orders.length > 0 && (
            <Link to="/account/orders" className="text-xs font-semibold text-rose-500 hover:underline inline-flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
          )}
        </div>
        {recent.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mb-4">
              <Package className="w-6 h-6" />
            </div>
            <div className="font-semibold text-zinc-900">No orders yet</div>
            <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">Browse our products and place your first order — it'll appear here with tracking and reorder options.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 mt-5 bg-zinc-900 text-white px-5 py-2.5 rounded-lg text-xs font-bold hover:bg-rose-500 transition-colors">
              <ShoppingBag className="w-3.5 h-3.5" /> Shop products
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {recent.map((o) => {
              const Icon = statusIcon[o.status] || Package;
              return (
                <li key={o.id} className="px-6 py-4 flex items-center gap-4">
                  <img src={o.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-zinc-50 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-zinc-900 truncate">{o.product}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${statusBg[o.statusColor]}`}>
                        <Icon className="w-3 h-3 inline mr-1 -mt-0.5" />{o.status}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">Order {o.id} · {o.qty} units · placed {o.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-zinc-900">${o.total.toFixed(2)}</div>
                    <Link to="/account/orders" className="text-[11px] font-semibold text-rose-500 hover:underline">Details</Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Saved designs quick view */}
      <div className="bg-white rounded-2xl border border-zinc-200 mt-6">
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
          <h2 className="font-bold text-zinc-900">Your saved designs</h2>
          {designs.length > 0 && (
            <Link to="/account/designs" className="text-xs font-semibold text-rose-500 hover:underline inline-flex items-center gap-1">Browse all <ArrowRight className="w-3 h-3" /></Link>
          )}
        </div>
        {designs.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-violet-50 text-violet-500 flex items-center justify-center mb-4">
              <Palette className="w-6 h-6" />
            </div>
            <div className="font-semibold text-zinc-900">No saved designs yet</div>
            <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">Designs uploaded during checkout are saved here automatically — reuse them for instant reorders.</p>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {designs.slice(0, 4).map((d) => (
              <div key={d.id} className="group">
                <div className="aspect-square rounded-lg overflow-hidden bg-zinc-100">
                  <img src={d.thumb} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="mt-2 text-xs font-semibold text-zinc-800 truncate">{d.name}</div>
                <div className="text-[11px] text-zinc-500">{d.productName}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New-user CTA bar */}
      {orders.length === 0 && (
        <div className="mt-6 rounded-2xl bg-gradient-to-r from-rose-500 to-orange-500 text-white p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-lg">Get 20% off your first order</div>
              <div className="text-sm text-white/90 mt-0.5">Use code <span className="font-mono font-bold bg-white/15 px-1.5 py-0.5 rounded">DPLUS20</span> at checkout. Valid on orders of $40+.</div>
            </div>
          </div>
          <Link to="/shop" className="bg-white text-rose-600 px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-zinc-900 hover:text-white transition-colors whitespace-nowrap">Start shopping</Link>
        </div>
      )}
    </div>
  );
};

export default Overview;
